import { Injectable, computed, inject, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject, catchError, delay, map, of, switchMap, tap } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { createActionState, formValueMap } from '@at/shared';
import { TripApiService } from '../api/trip.api.service';
import { DatesApi } from '../modules/dates/api';
import { CartService } from '../../inspire/services/cart/cart.service';
import { CreateTripResponse } from '../api';

export type StepConfig = {
  name: string;
  order: number;
  form?: FormGroup;
  route: string;
};

@Injectable()
export class CreateTripService {
  readonly #tripApi = inject(TripApiService);
  readonly #cart = inject(CartService);
  readonly #datesApi = inject(DatesApi);
  readonly #createTrip = new Subject<void>();
  readonly #createTripForm = new FormGroup({
    identification: new FormGroup(
      {
        title: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
        description: new FormControl(''),
        itineraryId: new FormControl<string | null>(null),
        shareEmail: new FormControl<boolean>(false),
      },
      [Validators.required],
    ),
    dates: new FormGroup({
      start: new FormControl<Date | null>(null, {
        /* validators: [minDateValidator(new Date().toISOString().split('T')[0])], */
      }),
      end: new FormControl<Date | null>(null),
    }),
    location: new FormGroup({
      location: new FormControl<string | null>(null),
    }),
  });
  readonly #stepsConfig: StepConfig[] = [
    {
      name: 'Identification',
      form: this.#createTripForm.controls.identification,
      order: 0,
      route: 'name',
    },
    /* {
      name: 'Location',
      form: this.#createTripForm.controls.location,
      order: 1,
      route: 'location',
    }, */
    {
      name: 'Dates',
      form: this.#createTripForm.controls.dates,
      order: 1,
      route: 'dates',
    },
    {
      name: 'Summary',
      order: 2,
      route: 'summary',
    },
  ];

  public readonly summaryState = toSignal(
    createActionState({
      trigger: this.#createTrip.asObservable().pipe(formValueMap(this.#createTripForm)),
      apiCall: (value) =>
        this.#tripApi.createTrip(value.identification).pipe(
          tap(() => this.#cart.removeAllActivities()),
          switchMap((response: CreateTripResponse) => {
            if (!value.identification.shareEmail) return of(response);
            // we're not interested in the result of this call
            // however we should keep the observable chain so data is properly updated
            return this.#tripApi.contactOperator(value.identification.itineraryId ?? '', response.tripId).pipe(
              map(() => response),
              catchError(() => of(response)),
            );
          }),
          switchMap((response) => {
            const startDate = value.dates?.start;
            const endDate = value.dates?.end;
            if (!response || !startDate || !endDate) return of(response);

            return this.#datesApi
              .postRange({ start: startDate, end: endDate, minDuration: 1, notifyMembers: false }, response.tripId)
              .pipe(map(() => response));
          }),
        ),
      beforeStart: () => {
        if (this.#createTripForm.invalid) {
          this.#createTripForm.markAllAsTouched();
        }
      },
    }),
  );

  public currentIndex = signal(0);
  public currentStep = computed(() => {
    return this.#stepsConfig[this.currentIndex()];
  });

  public get trip() {
    return this.#createTripForm.getRawValue();
  }

  public next(navigateFn: (parts: string[]) => void) {
    const currentStepIndex = this.currentIndex();
    const currentStep = this.#stepsConfig[currentStepIndex];

    if (currentStep.form && currentStep.form.invalid) {
      currentStep.form.markAllAsTouched();
      return;
    }

    const nextStep = this.#stepsConfig[currentStepIndex + 1];
    navigateFn(['trip', 'create', nextStep.route]);
  }

  public createTrip() {
    this.#createTrip.next();
  }

  public createTripWithTitle(title: string, description: string, itinerary: string, shareEmail: boolean) {
    this.#createTripForm.controls.identification.get('title')?.setValue(title);
    this.#createTripForm.controls.identification.get('description')?.setValue(description);
    this.#createTripForm.controls.identification.get('itineraryId')?.setValue(itinerary);
    this.#createTripForm.controls.identification.get('shareEmail')?.setValue(shareEmail);
    this.createTrip();
  }
}
